import { CartService } from '@/common/services/cart/cart.service';
import { CurrentReservationService } from '@/common/services/reservation/current';
import { SummaryService } from '@/common/services/summary/summary.service';
import {
    Vehicle,
    VehicleFeature,
} from '@/core/domain/branches/models/branch.model';
import { GetCartUseCase } from '@/core/domain/cart/usecases/getCart.usecase';
import { SummaryResponse } from '@/core/domain/summary/models/summary-response.model';
import {
    ChargesSummary,
    FeesSummary,
    ProductSummary,
} from '@/core/domain/summary/models/summary.models';
import { GetSummaryUseCase } from '@/core/domain/summary/usecases/getSummary.usecase';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import { Component, Inject, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { of, switchMap } from 'rxjs';

@Component({
    selector: 'fe-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent implements OnInit {
    public loadingSummary = false;

    public loadingCart = false;

    public productsOpened = false;

    public chargesOpened = false;

    public feesOpened = false;

    public taxesOpened = false;

    public reservationDetailsOpened = true;

    public paymentSummaryOpened = true;

    public maxHeightCharges = 0;

    public maxHeightProducts = 0;

    public maxHeightFees = 0;

    public vehicleUpgradePromotion?: Vehicle;

    public isUpgradePromotion = false;

    constructor(
        private readonly store: Store,
        @Inject('DATE_FORMAT_PROGRESS_BAR')
        public dateFormatProgressBar: string,
        public reservation: CurrentReservationService,
        private readonly getSummaryUseCase: GetSummaryUseCase,
        public summaryService: SummaryService,
        private readonly getCartUseCase: GetCartUseCase,
        public cartService: CartService,
    ) {}

    public get isLite(): boolean {
        return this.summaryService.isLite;
    }

    public ngOnInit(): void {
        this.summaryService.summary = undefined;
        setTimeout(() => {
            this.initSummary();
            this.initCart();
        }, 500);
    }

    public get categoryFeatureIcons(): VehicleFeature[] {
        if (this.reservation.current?.origin === 'INT') {
            return [];
        }
        return (this.summaryService.summary?.category?.features ?? []).filter(
            (feature) => !!feature.img,
        );
    }

    public get couponPercentage(): number {
        const summary = this.summaryService.summary;
        const percentage = summary?.isPrePaid
            ? summary?.promotion?.discount
            : summary?.promotion?.discountOffice;
        return Number(percentage) || 0;
    }

    public getIncludesKey(): string {
        return this.isLite || !this.reservation.insurances?.[0]?.title
            ? 'fe_step5_summary_div_vehicle-includes_lite'
            : 'fe_step5_summary_div_vehicle-includes';
    }

    public getChargesTitleKey(): string {
        return this.isLite
            ? 'fe_step5_summary_div_title-charges_lite'
            : 'fe_step5_summary_div_title-charges';
    }

    public getTotalTitleKey(): string {
        if (this.reservation.current?.origin === 'INT') {
            return 'fe_step5_summary_div_title-total';
        }
        if (this.reservation.current?.flowType === 'prepaid') {
            return 'fe_step5_summary_div_title-total_prepaid';
        }
        return this.isLite
            ? 'fe_step5_summary_div_title-total_lite'
            : 'fe_step5_summary_div_title-total';
    }

    private initCart(): void {
        this.loadingCart = true;
        this.getCartUseCase
            .execute()
            .subscribe(() => (this.loadingCart = false));
    }

    private initSummary(): void {
        this.loadingSummary = true;
        this.getSummaryUseCase.execute().subscribe((responseSummary) => {
            this.calculateMaxHeightCharges(responseSummary);
            this.calculateMaxHeightProduts(responseSummary);
            this.calculateMaxHeightFees(responseSummary);
            this.loadingSummary = false;
        });
        this.store
            .select(selectReservation)
            .pipe(
                switchMap((reservation) => {
                    this.isUpgradePromotion = reservation.isUpgradePromotion;
                    this.vehicleUpgradePromotion =
                        reservation.upgradeCatergoryPromotion;
                    return of(reservation);
                }),
            )
            .subscribe();
    }

    private calculateMaxHeightCharges(responseSummary: SummaryResponse): void {
        let items = _.get(responseSummary, 'data.products', []);
        items = _.filter(
            items,
            (item: ProductSummary) => item.value !== undefined,
        );
        this.maxHeightProducts = 70 * items.length;
    }

    private calculateMaxHeightFees(responseSummary: SummaryResponse): void {
        let items = _.get(responseSummary, 'data.fees', []);
        items = _.filter(
            items,
            (item: FeesSummary) => item.value !== undefined && item.value > 0,
        );
        this.maxHeightFees = 70 * items.length;
    }

    private calculateMaxHeightProduts(responseSummary: SummaryResponse): void {
        let allCharges = _.get(responseSummary, 'data.charges', []);
        allCharges = _.filter(
            allCharges,
            (item: ChargesSummary) => item.value !== undefined,
        );
        this.maxHeightCharges = 50 * allCharges.length;
    }

    public onClickCharges(): void {
        this.chargesOpened = !this.chargesOpened;
    }

    public onClickFees(): void {
        this.feesOpened = !this.feesOpened;
    }

    public onClickProducts(): void {
        this.productsOpened = !this.productsOpened;
    }

    public onClickTaxes(): void {
        this.taxesOpened = !this.taxesOpened;
    }

    public onClickReservationDetails(): void {
        this.reservationDetailsOpened = !this.reservationDetailsOpened;
    }

    public onClickPaymentSummary(): void {
        this.paymentSummaryOpened = !this.paymentSummaryOpened;
    }
}
